import React, { useState, useRef } from 'react';
import { Modal, message } from 'antd';
import { useParams } from 'react-router-dom';
import { theme } from 'utils/style/theme';
import VerticalForm from 'components/form/DefaultForm';
import DefaultRadio from 'components/input/DefaultRadio';
import DefaultInput from 'components/input/DefaultInput';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PrimaryButton from 'components/button/PrimaryButton';
import { ICoupon } from '../../../utils/types/operationTypes';
import { couponApis } from '../../../apis/operation/couponApis';

interface IProps {
  visible: boolean;
  setVisible: (e: any) => void;
  fetchData: () => void;
}

const CouponRegisterModal: React.FC<IProps> = ({
  visible,
  setVisible,
  fetchData,
}) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [inputData, setInputData] = useState<ICoupon>({
    id: 0,
    coupon_name: '',
    project_id: Number(projectId),
    coupon_type: 'keyword',
    keyword: '',
    rewards: [],
    issue_count: 10,
    remain_keyword_count: 10,
    start_date: '',
    end_date: '',
  });

  /// 인풋
  const handleInputChange = (e) => {
    setInputData({ ...inputData, [e.target.name]: e.target.value });
  };

  const [loading, setLoading] = useState(false);

  /// 등록하기
  const handleSubmit = async () => {
    if (inputData?.coupon_name === '') {
      message.error('쿠폰 명을 입력하세요.', 1);
      return;
    }

    if (!inputData?.start_date) {
      message.error('시작일을 입력하세요.', 1);
      return;
    }

    if (!inputData?.end_date) {
      message.error('종료일을 입력하세요.', 1);
      return;
    }

    try {
      setLoading(true);
      if (inputData.coupon_type === 'keyword') {
        await couponApis.createKeywordCoupon(Number(projectId), inputData);
      } else {
        await couponApis.createSerialCoupon(Number(projectId), inputData);
      }

      setVisible(false);
      fetchData();
      setLoading(false);
      message.success('쿠폰 등록 완료', 1);
    } catch (err) {
      setLoading(false);
      console.log('create episode error:', err);
    }
  };

  /// 모달 닫기
  const handleCancel = () => {
    setVisible(false);
  };

  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.episodeContent.header.registerEpisodeModal.episodeRegistration',
      )}
      visible={visible}
      onCancel={handleCancel}
      footer={
        <FooterBtnWrapper>
          <PrimaryButton
            text={t(
              'storyEpisodeID.storyTabMenuList.episodeContent.header.registerEpisodeModal.register',
            )}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.primary}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.primary}`}
            rounded='6px'
            hoverBgColor={theme.colors.primary}
            activeBgColor={theme.colors.primary}
            selectedBgColor={theme.colors.primary}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </FooterBtnWrapper>
      }
    >
      <VerticalForm margin='0'>
        <DefaultRadio
          title={'타입'}
          name='coupon_type'
          optionList={[
            { key: 0, value: 'keyword', text: '키워드' },
            { key: 1, value: 'serial', text: '시리얼' },
          ]}
          defaultValue={inputData.coupon_type}
          onChange={handleInputChange}
        />

        <DefaultInput
          title={'쿠폰명'}
          name='coupon_name'
          maxLength={60}
          value={inputData.coupon_name}
          onChange={handleInputChange}
          placeholder={'KMS 출시 기념 쿠폰'}
        />

        <>
          {inputData.coupon_type === 'keyword' && (
            <DefaultInput
              title={'키워드'}
              name='keyword'
              maxLength={60}
              value={inputData.keyword}
              onChange={handleInputChange}
            />
          )}
        </>

        <DefaultInput
          title={'발급 수량'}
          name='issue_count'
          type='number'
          value={inputData.issue_count.toString()}
          onChange={handleInputChange}
          placeholder={'10'}
        />

        <DefaultInput
          title={'시작일'}
          name='start_date'
          type='date'
          value={inputData.start_date.toString()}
          onChange={handleInputChange}
          placeholder={'2021-01-01'}
        />

        <DefaultInput
          title={'종료일'}
          name='end_date'
          type='date'
          value={inputData.end_date.toString()}
          onChange={handleInputChange}
          placeholder={'2021-01-01'}
        />
      </VerticalForm>
    </CustomModal>
  );
};

export default CouponRegisterModal;

const CustomModal = styled(Modal)`
  .length-subtext {
    font-size: ${theme.fontSizes.sm};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    margin-top: 8px !important;
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.normal};
    color: ${theme.colors.dark_200};
  }

  .ant-modal-body {
    padding: 0;
  }

  label {
    margin-left: 0;
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.medium};
    line-height: 20px;
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_500};
    margin-bottom: 8px !important;
  }

  .ant-modal-content {
    box-shadow: none;
  }

  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }

  .ant-modal-footer {
    margin-top: 10px !important;
  }

  .ant-input-show-count-suffix {
    display: none;
  }
`;

const FooterBtnWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
